// "change my security questions": every active question from the Security
// Questions Registry in a scrolling list with checkboxes (exactly 2), then an
// answer box for each. The answers are hidden while typed, never shown in the
// chat and sent once, with the 2 registry ids; the back end hashes them.
import React, { useEffect, useState } from "react";
import EyeIcon from "./EyeIcon";
import { LIST } from "./listStyle";

export default function SecurityQuestionsModal({ open, data, busy, onSubmit, onCancel }) {
  const questions = Array.isArray(data?.questions) ? data.questions : [];
  const required = data?.required || 2;
  const maxChars = data?.maxAnswerChars || 120;
  const [picked, setPicked] = useState([]);
  const [answers, setAnswers] = useState({});
  const [filter, setFilter] = useState("");
  const [shown, setShown] = useState({}); // question id -> answer visible
  useEffect(() => { if (open) { setPicked([]); setAnswers({}); setFilter(""); setShown({}); } }, [open]);
  if (!open) return null;

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : p.length < required ? [...p, id] : p));
  const chosen = picked.map((id) => questions.find((q) => q.id === id)).filter(Boolean);
  const ready = chosen.length === required && chosen.every((q) => String(answers[q.id] || "").trim());
  const listed = filter.trim() ? questions.filter((q) => q.question.toLowerCase().includes(filter.trim().toLowerCase())) : questions;
  const submit = (e) => {
    if (e) e.preventDefault();
    if (!ready || busy) return;
    onSubmit({ questionIds: chosen.map((q) => q.id), answers: chosen.map((q) => answers[q.id]) });
  };

  return (
    <div role="dialog" aria-modal="true" aria-label="Choose your security questions"
      style={{ ...LIST.backdrop, zIndex: 9800 }}>
      <form onSubmit={submit} style={{ ...LIST.card, maxHeight: "92vh", overflow: "hidden", gap: "8px" }}>
        <button type="button" onClick={onCancel} aria-label="Close" disabled={busy} style={LIST.close}>✕</button>
        <div style={LIST.title}>Choose {required} security questions</div>
        <div style={{ ...LIST.sub, fontSize: "13px" }}>{data?.title || "Tick 2 questions, then answer them."} ({chosen.length} of {required} chosen)</div>
        <input type="search" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Find a question"
          aria-label="Find a question"
          style={{ padding: "8px 10px", borderRadius: "10px", border: "1px solid #cce", fontSize: "14px" }} />
        <div role="group" aria-label="Security questions" style={{ ...LIST.list, flex: "1 1 auto", minHeight: 120, overflowY: "auto" }}>
          {listed.map((q) => {
            const on = picked.includes(q.id);
            const full = !on && picked.length >= required;
            return (
              <label key={q.id} style={{ ...LIST.row, ...(on ? LIST.rowSelected : {}), ...(full ? LIST.rowDisabled : {}), alignItems: "flex-start", cursor: full ? "not-allowed" : "pointer" }}>
                <input type="checkbox" checked={on} disabled={full || busy} onChange={() => toggle(q.id)} style={{ marginTop: 3 }} />
                <span style={{ flex: 1 }}>{q.question}</span>
              </label>
            );
          })}
          {!listed.length && <p style={LIST.empty}>No question matches.</p>}
        </div>
        {chosen.map((q, i) => (
          <div key={q.id} style={{ marginTop: 10 }}>
            <label style={{ display: "block", fontSize: "13px", fontWeight: 700, marginBottom: 4 }}>{i + 1}. {q.question}</label>
            <div style={{ position: "relative" }}>
              <input type={shown[q.id] ? "text" : "password"} autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck={false}
                maxLength={maxChars} value={answers[q.id] || ""} disabled={busy}
                onChange={(e) => setAnswers((a) => ({ ...a, [q.id]: e.target.value }))}
                aria-label={`Answer to: ${q.question}`}
                style={{ width: "100%", boxSizing: "border-box", padding: "9px 40px 9px 10px", borderRadius: "10px", border: "1px solid #cce", fontSize: "15px" }} />
              <button type="button" onClick={() => setShown((v) => ({ ...v, [q.id]: !v[q.id] }))}
                aria-label={shown[q.id] ? "Hide answer" : "Show answer"} aria-pressed={!!shown[q.id]} title={shown[q.id] ? "Hide answer" : "Show answer"}
                style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", border: "none", background: "transparent", color: "#4f8dbf", cursor: "pointer", padding: 4, display: "flex" }}>
                <EyeIcon open={!!shown[q.id]} />
              </button>
            </div>
          </div>
        ))}
        {data?.error && <div role="alert" style={{ color: "#b3261e", fontSize: "13px", marginTop: 8 }}>{data.error}</div>}
        <div style={{ fontSize: "12px", marginTop: 8 }}>Your answers are stored securely and never shown again.</div>
        <button type="submit" disabled={!ready || busy}
          style={{ marginTop: 12, padding: "10px", borderRadius: "12px", border: "none", background: ready && !busy ? "#4f8dbf" : "#9cc4e4", color: "#fff", fontWeight: 800, fontSize: "15px", cursor: ready && !busy ? "pointer" : "not-allowed" }}>
          {busy ? "Saving…" : "Save"}
        </button>
      </form>
    </div>
  );
}
